import { css } from '@emotion/react'
import { useState } from 'react'
import { inject, observer } from 'mobx-react'
import styled from '@emotion/styled'
import _ from 'lodash'
import dayjs from 'dayjs'

import { SECURITY_TYPES_TEXT, TAX_TYPES, VALUATION_METHODS } from '../../../../constants'
import Close from '../../../../assets/icons/Close'
import {
  CurrencyText,
  DeleteModal,
  PlanOffersOptionModal,
  ConfirmModal,
  SVGWrapper,
} from '../../../../components'
import { placeholderSymbolReplace, withRouterProps } from '../../../../utils'
import { CloseIcon, InvestmentListItemContainer, InvestmentListItemBox } from '../../styled'
import { API } from '../../../../api'
import { typeToAPI } from '../../utils'

const amountWrapsAtWidth = 990
const sidebarWidth = 0
const breakpoint = p =>
  p.sidebarOpen ? `${amountWrapsAtWidth}px` : `${amountWrapsAtWidth - sidebarWidth}px`

const FlexRowBetween = styled.div`
  display: flex;
  flex: wrap;
  flex-direction: row;
  width: 100%;

  @media (max-width: ${breakpoint}) {
    flex-direction: column;
  }
`

export const Type = styled.div`
  flex: 0 0 20%;
  padding: 0 8px;
  word-wrap: break-word;
  white-space: pre-wrap;

  @media (max-width: ${breakpoint}) {
    width: 100%;
    flex-basis: auto;
  }
`

export const Ticker = styled.div`
  flex: 0 0 20%;
  padding: 0 8px;
  word-wrap: break-word;
  white-space: pre-wrap;

  @media (max-width: ${breakpoint}) {
    width: 100%;
    flex-basis: auto;
  }
`

export const Name = styled.div`
  flex: 1 1 40%;
  padding: 0 8px;
  word-wrap: break-word;
  white-space: pre-wrap;

  @media (max-width: ${breakpoint}) {
    width: 100%;
    flex-basis: auto;
  }
`

const Amount = styled.div`
  flex: 1 0 auto;
  padding: 0 8px;
  text-align: right;
  word-wrap: break-word;
  white-space: pre-wrap;

  @media (max-width: ${breakpoint}) {
    flex-basis: auto;
    text-align: left;
    width: 100%;
  }
`

const Edit = styled.div`
  color: ${p => p.theme.editColor};
  @media (max-width: ${breakpoint}) {
    padding: 0 8px;
  }
`

const InvestmentGroupItem = props => {
  const [deleteModal, setDeleteModal] = useState('closed') // closed, planOffer, confirmPlanOffer, confirmNonPlanOffer, confirm
  const [status, setStatus] = useState('done')

  const handleModalClose = () => {
    setDeleteModal('closed')
    setStatus('done')
  }

  const toggleDeleteModal = e => {
    e.stopPropagation()

    const {
      store,
      match: {
        params: { id: accountId },
      },
      investment,
    } = props

    const { employerAccounts = [] } = store
    const isEmployerAccount = !!employerAccounts.find(account => account.id === Number(accountId))

    if (isEmployerAccount) {
      const { investments } = props.account
      const lastSecurityLeft =
        investments.filter(
          inv =>
            inv.securities?.id === investment.securities?.id &&
            inv.taxType !== TAX_TYPES.notInvested
        ).length === 1

      if (lastSecurityLeft) {
        setDeleteModal('planOffer')
        return
      }
    }

    setDeleteModal('confirm')
  }

  const handlePlanOffer = stillOffered => {
    setDeleteModal(stillOffered ? 'confirmPlanOffer' : 'confirmNonPlanOffer')
  }

  const handleConfirmDelete = async () => {
    setStatus('loading')

    try {
      const {
        features,
        store,
        store: { institutionalAccount },
        match: {
          params: { type, id },
        },
        account: { getAvailableInvestments },
        investment,
      } = props
      const isInstitutionalAccount = props.account?.account?.id === institutionalAccount?.id

      if (deleteModal === 'confirmPlanOffer') {
        await API.put(`${typeToAPI[type]}/${id}/investments`, {
          ...investment,
          dateOfUserInfo: dayjs().format('YYYY-MM-DD'),
          quantity: 0,
          totalCostBasis: 0,
          totalValue: 0,
          valuationMethod: VALUATION_METHODS.notInvested,
          taxType: TAX_TYPES.notInvested,
        })
      } else {
        await API.delete(`${typeToAPI[type]}/${id}/investments/${investment.id}`)
      }

      await store.getAccounts()
      if (isInstitutionalAccount && features.editInstitutionalAccount) {
        await getAvailableInvestments()
      }

      await props.account.getInvestments({ type, id })

      handleModalClose()
    } catch (err) {
      console.error(err)
      setStatus('error')
    }
  }

  const { canEdit, toggleEdit } = props
  const { taxType, value, securities: security, id } = props.investment
  const { securityType, securityName, ticker } = security
  const securityTypeName = SECURITY_TYPES_TEXT[securityType]

  return (
    <InvestmentListItemContainer>
      {canEdit && (
        <CloseIcon onClick={toggleDeleteModal}>
          <SVGWrapper margin='9px' size='large' svg={Close} />
        </CloseIcon>
      )}
      <InvestmentListItemBox>
        <FlexRowBetween sidebarOpen={false}>
          <Type>{securityTypeName}</Type>
          <Ticker>{ticker}</Ticker>
          <Name>{placeholderSymbolReplace(securityName)}</Name>
          {taxType !== TAX_TYPES.notInvested && (
            <>
              <Amount sidebarOpen={false}>
                <CurrencyText decimalScale={2} value={value} />
              </Amount>
              {canEdit && (
                <Edit sidebarOpen={false}>
                  <a
                    onClick={event => toggleEdit(id, event)}
                    css={css`
                      cursor: pointer;
                    `}>
                    Edit
                  </a>
                </Edit>
              )}
            </>
          )}
        </FlexRowBetween>
      </InvestmentListItemBox>
      <DeleteModal
        handleConfirmation={handleConfirmDelete}
        isModalOpen={deleteModal === 'confirm'}
        title={'Delete investment: ' + placeholderSymbolReplace(securityName) + '?'}
        toggleModal={handleModalClose}
        loading={status === 'loading'}
        error={status === 'error'}
      />
      <PlanOffersOptionModal
        isModalOpen={deleteModal === 'planOffer'}
        handleCancel={handleModalClose}
        handleConfirmation={() => handlePlanOffer(true)}
        handleNotOffered={() => handlePlanOffer(false)}
        title='Does your plan still offer this as an option for you to invest in?'
      />
      <ConfirmModal
        handleConfirm={handleConfirmDelete}
        isModalOpen={_.includes(['confirmPlanOffer', 'confirmNonPlanOffer'], deleteModal)}
        toggleModal={handleModalClose}
        title='Got it. Thanks.'
        secondaryText='Your investment will be removed. This action cannot be undone.'
        confirmButtonText='Save'
        denyButtonText='Cancel'
      />
    </InvestmentListItemContainer>
  )
}

export default withRouterProps(inject('store', 'account')(observer(InvestmentGroupItem)))
